
import { supabase, handleSupabaseError, getCurrentUser } from '@/lib/supabase'
import { getCachedUser, clearUserCache } from '@/utils/authCache'
import { extractStateFromLocation } from '@/utils/locationUtils'

// Import helper functions for JSONB operations
import {
  getTrackedPrograms,
  getTrackedProgramsByStatus,
  getTrackedProgramsByYear,
  getTrackedProgramById,
  addTrackedProgram,
  updateTrackedProgram,
  deleteTrackedProgram,
  countTrackedPrograms,
  getTrackedProgramsByChildren,
  bulkUpdateTrackedPrograms
} from '@/lib/database/tracked-programs-helpers'

import {
  getBookedCamps,
  getBookedCampsByStatus,
  getBookedCampsByChild,
  getBookedCampById,
  addBookedCamp,
  updateBookedCamp,
  deleteBookedCamp,
  countBookedCamps
} from '@/lib/database/booked-camps-helpers'

// Helper function for robust JSONB queries
const buildJsonbQuery = (query, field, key, value) => {
  try {
    if (value === null || value === undefined) {
      // Handle null values
      return query.is(`${field}->>${key}`, null);
    } else if (typeof value === 'string') {
      // For string values, use contains for exact match
      return query.contains(field, { [key]: value });
    } else if (Array.isArray(value)) {
      // For array values, use contains
      return query.contains(field, { [key]: value });
    } else if (typeof value === 'object') {
      // For object values, use contains
      return query.contains(field, { [key]: value });
    } else {
      // For primitive values (numbers, booleans), use contains
      return query.contains(field, { [key]: value });
    }
  } catch (error) {
    console.error('JSONB query build error:', error);
    // Fallback to simple contains
    return query.contains(field, { [key]: value });
  }
};

// Camp entity - handles camp/program data from camp_audit_data table
export const Camp = {
  /**
   * Get camps near a specific location using PostGIS
   * @param {number} latitude - User's latitude
   * @param {number} longitude - User's longitude
   * @param {number} radiusMiles - Search radius in miles (default: 50)
   * @param {number} maxResults - Maximum number of results (default: 100)
   * @returns {Promise<Array>} Array of camps with distance_miles included
   */
  async nearLocation(latitude, longitude, radiusMiles = 50, maxResults = 100) {
    try {
      console.log('Calling get_camps_near_location with params:', {
        user_lat: latitude,
        user_lng: longitude,
        radius_miles: radiusMiles,
        max_results: maxResults
      });

      const { data, error } = await supabase.rpc('get_camps_near_location', {
        user_lat: latitude,
        user_lng: longitude,
        radius_miles: radiusMiles,
        max_results: maxResults
      });

      if (error) {
        console.error('PostGIS RPC Error:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      console.log('PostGIS query successful, returned', data?.length || 0, 'camps');
      
      // Fetch camp_type for all camps since RPC doesn't return it
      // This is necessary because the PostGIS RPC doesn't select camp_type
      let campTypeMap = {};
      if (data && data.length > 0) {
        try {
          const campIds = data.map(camp => camp.id).filter(Boolean);
          if (campIds.length > 0) {
            const { data: campTypesData, error: campTypesError } = await supabase
              .from('camp_audit_data')
              .select('id, camp_type')
              .in('id', campIds);
            
            if (!campTypesError && campTypesData) {
              campTypeMap = Object.fromEntries(
                campTypesData.map(ct => [ct.id, ct.camp_type])
              );
            }
          }
        } catch (err) {
          console.warn('Failed to fetch camp_type for camps:', err);
        }
      }
      
      // Map the database columns to expected format
      const mappedCamps = (data || []).map(camp => ({
        ...camp,
        // Preserve camp_type from separate query since RPC doesn't return it
        camp_type: camp.camp_type || campTypeMap[camp.id] || null,
        // Map coordinates_lat/lng to latitude/longitude for compatibility
        latitude: camp.latitude,
        longitude: camp.longitude,
        // Map other fields to expected names
        category: camp.category || camp.primary_category,
        price: camp.price || camp.cost,
        tags: camp.tags || camp.all_categories,
        // Add distance_miles if not already present
        distance_miles: camp.distance_miles
      }));
      
      return mappedCamps;
    } catch (error) {
      console.error('Camp.nearLocation failed completely:', error);
      throw error;
    }
  },

  /**
   * Get camps by state code
   * @param {string} stateCode - 2-letter uppercase state code (e.g., "CA", "NY")
   * @param {number} maxResults - Maximum number of results (default: 150)
   * @returns {Promise<Array>} Array of camps filtered by state
   */
  async byState(stateCode, maxResults = 150) {
    try {
      if (!stateCode || typeof stateCode !== 'string') {
        throw new Error('State code is required');
      }

      // Normalize state code to uppercase
      const normalizedStateCode = stateCode.trim().toUpperCase();
      if (normalizedStateCode.length !== 2) {
        throw new Error('State code must be 2 letters');
      }

      console.log('Calling Camp.byState with state code:', normalizedStateCode);

      // Escape special ILIKE characters (%, _)
      const escapedStateCode = normalizedStateCode.replace(/%/g, '\\%').replace(/_/g, '\\_');

      // Use word-boundary patterns to filter by state code in formatted_address
      // Match common address formats to avoid false positives:
      // - ", STATE_CODE " (comma, space, state code, space) - most common
      // - ", STATE_CODE" (comma, space, state code at end)
      // - " STATE_CODE " (space, state code, space)
      // - " STATE_CODE" (space, state code at end)
      // This prevents matching state codes as substrings (e.g., "IN" in "MAIN", "OR" in "NORTH")
      const { data, error } = await supabase
        .from('camp_audit_data')
        .select('*')
        .or(`formatted_address.ilike.%, ${escapedStateCode} %,formatted_address.ilike.%, ${escapedStateCode},formatted_address.ilike. ${escapedStateCode} %,formatted_address.ilike. ${escapedStateCode}`)
        .order('rating', { ascending: false, nullsFirst: false })
        .limit(maxResults);

      if (error) {
        console.error('Camp.byState query error:', error);
        throw error;
      }

      console.log('Camp.byState query successful, returned', data?.length || 0, 'camps');

      // Map database columns to expected format for consistency
      const mappedCamps = (data || []).map(camp => ({
        ...camp,
        // Map coordinates_lat/lng to latitude/longitude for compatibility
        latitude: camp.coordinates_lat,
        longitude: camp.coordinates_lng,
        // Map other fields to expected names
        category: camp.primary_category,
        price: camp.cost,
        tags: camp.all_categories,
        // Parse age_range if it exists (format: "5-12" or "8-15")
        age_min: camp.age_range ? parseInt(camp.age_range.split('-')[0]) : null,
        age_max: camp.age_range ? parseInt(camp.age_range.split('-')[1]) : null
      }));

      return mappedCamps;
    } catch (error) {
      console.error('Camp.byState failed:', error);
      handleSupabaseError(error);
    }
  },

  /**
   * Get camps for perfect matches with intelligent filtering
   * - Day camps use day_camp radius preference
   * - Overnight/both camps use overnight_camp radius preference
   * - Includes camps without coordinates but matching user's state
   * @param {Object} userProfile - User profile with locations and family_preferences
   * @param {number} maxResults - Maximum number of results (default: 200)
   * @returns {Promise<Array>} Array of camps for perfect matching
   */
  async forPerfectMatches(userProfile, maxResults = 200) {
    try {
      // Extract user home location
      const homeLocation = userProfile?.locations?.home;
      const homeLat = homeLocation?.lat ?? homeLocation?.latitude;
      const homeLng = homeLocation?.lng ?? homeLocation?.longitude;
      const hasCoordinates = homeLat != null && homeLng != null && !isNaN(homeLat) && !isNaN(homeLng);

      // Extract preferences
      const prefs = userProfile?.family_preferences || {};
      const dayCampRadius = prefs?.day_camp?.max_distance_miles || 25;
      const overnightRadius = prefs?.overnight_camp?.max_distance_miles || 400;

      // Extract state from user location
      const userState = extractStateFromLocation(homeLocation);

      console.log('🔍 Camp.forPerfectMatches starting:', {
        hasCoordinates,
        homeLat,
        homeLng,
        dayCampRadius,
        overnightRadius,
        userState,
        maxResults
      });

      const allCamps = [];
      const campIds = new Set(); // For deduplication

      // Helper to add camp if not already added
      const addCamp = (camp) => {
        if (camp && camp.id && !campIds.has(camp.id)) {
          campIds.add(camp.id);
          allCamps.push(camp);
        }
      };

      // Helper to map camp fields consistently
      const mapCamp = (camp) => ({
        ...camp,
        camp_type: camp.camp_type || null,
        latitude: camp.coordinates_lat ?? camp.latitude,
        longitude: camp.coordinates_lng ?? camp.longitude,
        category: camp.category || camp.primary_category,
        price: camp.price || camp.cost,
        tags: camp.tags || camp.all_categories,
        age_min: camp.age_min || (camp.age_range ? parseInt(camp.age_range.split('-')[0]) : null),
        age_max: camp.age_max || (camp.age_range ? parseInt(camp.age_range.split('-')[1]) : null),
        distance_miles: camp.distance_miles || null
      });

      // 1. Fetch day camps with coordinates (using day camp radius)
      if (hasCoordinates) {
        try {
          console.log('📍 Fetching day camps with coordinates (radius:', dayCampRadius, 'miles)');
          const { data: dayCampsData, error: dayCampsError } = await supabase.rpc('get_camps_near_location', {
            user_lat: homeLat,
            user_lng: homeLng,
            radius_miles: dayCampRadius,
            max_results: maxResults
          });

          if (!dayCampsError && dayCampsData) {
            // Fetch camp_type for day camps and filter to only day_camp type
            const dayCampIds = dayCampsData.map(c => c.id).filter(Boolean);
            if (dayCampIds.length > 0) {
              const { data: campTypesData } = await supabase
                .from('camp_audit_data')
                .select('id, camp_type')
                .in('id', dayCampIds);

              const campTypeMap = Object.fromEntries(
                (campTypesData || []).map(ct => [ct.id, ct.camp_type])
              );

              // Filter to day_camp, both, or null (include null since type is unknown)
              dayCampsData
                .filter(camp => {
                  const campType = campTypeMap[camp.id] || camp.camp_type;
                  return campType === 'day_camp' || campType === 'both' || campType === null;
                })
                .forEach(camp => {
                  const campType = campTypeMap[camp.id] || camp.camp_type;
                  addCamp(mapCamp({ ...camp, camp_type: campType || 'day_camp' }));
                });
            }
          } else if (dayCampsError) {
            console.warn('⚠️ Error fetching day camps:', dayCampsError);
          }
        } catch (err) {
          console.warn('⚠️ Exception fetching day camps:', err);
        }
      }

      // 2. Fetch overnight/both camps with coordinates (using overnight radius)
      if (hasCoordinates) {
        try {
          console.log('🏕️ Fetching overnight/both camps with coordinates (radius:', overnightRadius, 'miles)');
          const { data: overnightCampsData, error: overnightCampsError } = await supabase.rpc('get_camps_near_location', {
            user_lat: homeLat,
            user_lng: homeLng,
            radius_miles: overnightRadius,
            max_results: maxResults
          });

          if (!overnightCampsError && overnightCampsData) {
            // Fetch camp_type for overnight camps and filter to overnight_camp or both
            const overnightCampIds = overnightCampsData.map(c => c.id).filter(Boolean);
            if (overnightCampIds.length > 0) {
              const { data: campTypesData } = await supabase
                .from('camp_audit_data')
                .select('id, camp_type')
                .in('id', overnightCampIds);

              const campTypeMap = Object.fromEntries(
                (campTypesData || []).map(ct => [ct.id, ct.camp_type])
              );

              // Filter to overnight_camp, both, or null (include null since type is unknown)
              overnightCampsData
                .filter(camp => {
                  const campType = campTypeMap[camp.id] || camp.camp_type;
                  return campType === 'overnight_camp' || campType === 'both' || campType === null;
                })
                .forEach(camp => {
                  const campType = campTypeMap[camp.id] || camp.camp_type;
                  addCamp(mapCamp({ ...camp, camp_type: campType }));
                });
            }
          } else if (overnightCampsError) {
            console.warn('⚠️ Error fetching overnight camps:', overnightCampsError);
          }
        } catch (err) {
          console.warn('⚠️ Exception fetching overnight camps:', err);
        }
      }

      // 3. Fetch camps without coordinates but matching state
      // Treat camps with partial coordinates (one null) as "no coordinates"
      if (userState) {
        try {
          console.log('🗺️ Fetching camps without coordinates in state:', userState);
          const escapedStateCode = userState.replace(/%/g, '\\%').replace(/_/g, '\\_');
          
          // Query camps that have no coordinates (at least one null) AND match the state
          // First, get camps with no coordinates
          const { data: noCoordsCamps, error: noCoordsError } = await supabase
            .from('camp_audit_data')
            .select('*')
            .or(`coordinates_lat.is.null,coordinates_lng.is.null`)
            .limit(200); // Get more to filter by state
          
          if (!noCoordsError && noCoordsCamps) {
            // Filter to camps that match state pattern and have both coordinates null
            const statePattern = new RegExp(`(?:,|\\s)${escapedStateCode}(?:\\s|,|$)`, 'i');
            const campsWithoutCoords = noCoordsCamps.filter(camp => {
              const hasNoCoords = (!camp.coordinates_lat || camp.coordinates_lat === null) && 
                                  (!camp.coordinates_lng || camp.coordinates_lng === null);
              const matchesState = camp.formatted_address && statePattern.test(camp.formatted_address);
              return hasNoCoords && matchesState;
            }).slice(0, 50); // Limit to 50

            campsWithoutCoords.forEach(camp => {
              addCamp(mapCamp(camp));
            });
            console.log(`✅ Found ${campsWithoutCoords.length} camps without coordinates in ${userState}`);
          } else if (noCoordsError) {
            console.warn('⚠️ Error fetching camps without coordinates:', noCoordsError);
          }
        } catch (err) {
          console.warn('⚠️ Exception fetching camps by state:', err);
        }
      }

      // Limit to maxResults
      const finalCamps = allCamps.slice(0, maxResults);

      // Fetch full camp_audit_data rows so transformDatabaseCamp gets community/contribution fields (RPC may return partial columns)
      if (finalCamps.length > 0) {
        try {
          const ids = finalCamps.map(c => c.id).filter(Boolean);
          if (ids.length > 0) {
            const { data: fullRows, error: fullError } = await supabase
              .from('camp_audit_data')
              .select('*')
              .in('id', ids);
            if (!fullError && fullRows?.length > 0) {
              const fullById = new Map(fullRows.map(r => [r.id, r]));
              // Merge full DB row with RPC result: full row provides community/contribution columns,
              // while preserving RPC-computed fields (distance_miles, distance) used by PerfectMatchHorizontalCard
              const enriched = finalCamps.map(c => {
                const fullRow = fullById.get(c.id);
                if (!fullRow) return c;
                return {
                  ...fullRow,
                  distance_miles: c.distance_miles ?? fullRow.distance_miles,
                  distance: c.distance ?? fullRow.distance
                };
              });
              console.log(`✅ Camp.forPerfectMatches returning ${enriched.length} camps (full rows for community/contribution display)`);
              return enriched;
            }
          }
        } catch (enrichErr) {
          console.warn('⚠️ forPerfectMatches full-row enrichment failed, returning partial rows:', enrichErr);
        }
      }

      console.log(`✅ Camp.forPerfectMatches returning ${finalCamps.length} camps (${allCamps.length} total before limit)`);
      return finalCamps;
    } catch (error) {
      console.error('❌ Camp.forPerfectMatches failed:', error);
      // Fallback to list if everything fails
      try {
        return await this.list('-rating', 50);
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError);
        return [];
      }
    }
  },

  async list(orderBy = '-created_at', limit = null) {
    try {
      let query = supabase.from('camp_audit_data').select('*')
      
      // Handle ordering - map common field names to new schema
      let orderField = orderBy
      if (orderBy.startsWith('-')) {
        orderField = orderBy.substring(1)
        // Map old field names to new schema
        if (orderField === 'created_date') orderField = 'created_at'
        if (orderField === 'rating') orderField = 'rating'
        query = query.order(orderField, { ascending: false })
      } else {
        if (orderBy === 'created_date') orderField = 'created_at'
        query = query.order(orderField, { ascending: true })
      }
      
      if (limit) {
        query = query.limit(limit)
      }
      
      const { data, error } = await query
      if (error) throw error
      
      // Map database columns to expected format for consistency
      const mappedCamps = (data || []).map(camp => ({
        ...camp,
        // Map coordinates_lat/lng to latitude/longitude for compatibility
        latitude: camp.coordinates_lat,
        longitude: camp.coordinates_lng,
        // Map other fields to expected names
        category: camp.primary_category,
        price: camp.cost,
        tags: camp.all_categories,
        // Parse age_range if it exists (format: "5-12" or "8-15")
        age_min: camp.age_range ? parseInt(camp.age_range.split('-')[0]) : null,
        age_max: camp.age_range ? parseInt(camp.age_range.split('-')[1]) : null
      }));

      return mappedCamps
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async get(id) {
    try {
      const { data, error } = await supabase
        .from('camp_audit_data')
        .select('*')
        .eq('id', id)
        .single()
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async create(campData) {
    try {
      const { data, error } = await supabase
        .from('camp_audit_data')
        .insert([campData])
        .select()
        .single()
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async update(id, updates) {
    try {
      const { data, error } = await supabase
        .from('camp_audit_data')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase
        .from('camp_audit_data')
        .delete()
        .eq('id', id)
      if (error) throw error
      return true
    } catch (error) {
      handleSupabaseError(error)
    }
  }
};

// Booking entity - handles user bookings (now using JSONB in profiles)
export const Booking = {
  async list(orderBy = '-booked_at', limit = null) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Use helper function to get booked camps
      let bookedCamps = await getBookedCamps(user.id)
      
      // Handle ordering
      if (orderBy.startsWith('-')) {
        const field = orderBy.substring(1)
        bookedCamps = bookedCamps.sort((a, b) => {
          const aVal = a[field] || ''
          const bVal = b[field] || ''
          return bVal.localeCompare(aVal)
        })
      } else {
        bookedCamps = bookedCamps.sort((a, b) => {
          const aVal = a[orderBy] || ''
          const bVal = b[orderBy] || ''
          return aVal.localeCompare(bVal)
        })
      }
      
      if (limit) {
        bookedCamps = bookedCamps.slice(0, limit)
      }
      
      return bookedCamps
    } catch (error) {
      console.error('Error in Booking.list:', error)
      return []
    }
  },

  async create(bookingData) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Use helper function to add booked camp
      return await addBookedCamp(user.id, {
        ...bookingData,
        created_by: user.email
      })
    } catch (error) {
      console.error('Error in Booking.create:', error)
      throw error
    }
  },

  async update(id, updates) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Use helper function to update booked camp
      return await updateBookedCamp(user.id, id, updates)
    } catch (error) {
      console.error('Error in Booking.update:', error)
      throw error
    }
  },

  async delete(id) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Use helper function to delete booked camp
      await deleteBookedCamp(user.id, id)
      return true
    } catch (error) {
      console.error('Error in Booking.delete:', error)
      throw error
    }
  }
};

// SocialPoll entity - handles social polls
export const SocialPoll = {
  async list(orderBy = 'created_date', limit = 50) {
    try {
      const { data, error } = await supabase
        .from('socialpoll')
        .select('*')
        .order(orderBy, { ascending: false })
        .limit(limit)
      
      if (error) throw error
      
      // Parse target_friend_emails if it's a JSON string (database may store as TEXT instead of TEXT[])
      const parsedData = (data || []).map(poll => {
        if (poll.target_friend_emails && typeof poll.target_friend_emails === 'string') {
          try {
            poll.target_friend_emails = JSON.parse(poll.target_friend_emails);
          } catch (e) {
            console.warn('Failed to parse target_friend_emails as JSON:', e, poll.target_friend_emails);
            // Try parsing as PostgreSQL array format: {"email1","email2"}
            try {
              // Remove curly braces and quotes, split by comma
              poll.target_friend_emails = poll.target_friend_emails.replace(/[{}"]/g, '').split(',').filter(Boolean);
            } catch (e2) {
              console.warn('Failed to parse target_friend_emails as PostgreSQL array:', e2);
              poll.target_friend_emails = [];
            }
          }
        }
        return poll;
      });
      
      return parsedData
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async filter(filters = {}) {
    try {
      let query = supabase
        .from('socialpoll')
        .select('*')
      
      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        query = query.eq(key, value)
      })
      
      const { data, error } = await query.order('created_date', { ascending: false })
      if (error) throw error
      
      // Parse target_friend_emails if it's a JSON string (database may store as TEXT instead of TEXT[])
      const parsedData = (data || []).map(poll => {
        if (poll.target_friend_emails && typeof poll.target_friend_emails === 'string') {
          try {
            poll.target_friend_emails = JSON.parse(poll.target_friend_emails);
          } catch (e) {
            console.warn('Failed to parse target_friend_emails as JSON:', e, poll.target_friend_emails);
            try {
              poll.target_friend_emails = poll.target_friend_emails.replace(/[{}"]/g, '').split(',').filter(Boolean);
            } catch (e2) {
              poll.target_friend_emails = [];
            }
          }
        }
        return poll;
      });
      
      return parsedData
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async create(pollData) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Map the data to match your schema - only include fields that exist in your table
      // Note: The table uses 'created_by' for email/id, not 'user_id'
      const mappedData = {
        camp_id: pollData.camp_id || null, // Optional - null for non-camp-specific polls
        question: pollData.question,
        status: pollData.status || 'active',
        votes: pollData.votes || [],
        created_by: user.email || user.id
      }

      // Add optional fields if they exist and have valid values
      // FIX: Include target_friend_emails if provided
      if (pollData.target_friend_emails && Array.isArray(pollData.target_friend_emails) && pollData.target_friend_emails.length > 0) {
        mappedData.target_friend_emails = pollData.target_friend_emails.map(email => String(email).trim().toLowerCase()).filter(Boolean);
      }
      
      // Include target_group_ids if provided and has valid data (UUID array)
      if (pollData.target_group_ids && Array.isArray(pollData.target_group_ids) && pollData.target_group_ids.length > 0) {
        // Ensure all IDs are valid UUIDs (convert strings to UUIDs if needed)
        mappedData.target_group_ids = pollData.target_group_ids.map(id => String(id));
      }
      
      if (pollData.carpool_interest !== undefined) {
        mappedData.carpool_interest = Boolean(pollData.carpool_interest);
      }

      // Log the data being sent for debugging
      console.log('Creating poll with data:', mappedData)

      const { data, error } = await supabase
        .from('socialpoll')
        .insert([mappedData])
        .select()
        .single()
      
      if (error) {
        console.error('Supabase error creating poll:', error)
        throw error
      }
      
      console.log('Poll created successfully:', data)
      return data
    } catch (error) {
      console.error('Error in SocialPoll.create:', error)
      handleSupabaseError(error)
      throw error // Re-throw so calling code can handle it
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase
        .from('socialpoll')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      
      // Parse target_friend_emails if it's a JSON string (database may store as TEXT instead of TEXT[])
      if (data && data.target_friend_emails && typeof data.target_friend_emails === 'string') {
        try {
          data.target_friend_emails = JSON.parse(data.target_friend_emails);
        } catch (e) {
          console.warn('Failed to parse target_friend_emails as JSON:', e, data.target_friend_emails);
          try {
            data.target_friend_emails = data.target_friend_emails.replace(/[{}"]/g, '').split(',').filter(Boolean);
          } catch (e2) {
            data.target_friend_emails = [];
          }
        }
      }
      
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async update(id, updates) {
    try {
      const payload = {}

      if (updates.question !== undefined) payload.question = updates.question
      if (updates.votes !== undefined) payload.votes = updates.votes
      // Note: target_friend_emails column does not exist in this database instance
      if (updates.target_group_ids !== undefined) payload.target_group_ids = updates.target_group_ids
      if (updates.status !== undefined) payload.status = updates.status
      if (updates.closed_date !== undefined) payload.closed_date = updates.closed_date
      if (updates.last_edited_date !== undefined) payload.last_edited_date = updates.last_edited_date
      if (updates.edit_count !== undefined) payload.edit_count = updates.edit_count

      payload.updated_at = new Date().toISOString()

      const { data, error } = await supabase
        .from('socialpoll')
        .update(payload)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async vote(pollId, voteData) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // First get the current poll to update votes array with better error handling
      const { data: poll, error: fetchError } = await supabase
        .from('socialpoll')
        .select('votes')
        .eq('id', pollId)
        .single()

      if (fetchError) {
        console.error('Error fetching poll for voting:', fetchError);
        throw fetchError;
      }

      // Safely handle JSONB votes array
      let currentVotes = [];
      try {
        currentVotes = Array.isArray(poll.votes) ? poll.votes : [];
      } catch (jsonError) {
        console.warn('Invalid votes JSONB data, resetting to empty array:', jsonError);
        currentVotes = [];
      }

      // Remove any existing vote from this user to prevent duplicates
      const filteredVotes = currentVotes.filter(vote =>
        vote.user_id !== user.id && vote.user_email !== user.email
      );

      const updatedVotes = [...filteredVotes, {
        ...voteData,
        user_id: user.id,
        user_email: user.email,
        created_at: new Date().toISOString()
      }];

      const { data, error } = await supabase
        .from('socialpoll')
        .update({ votes: updatedVotes })
        .eq('id', pollId)
        .select()

      if (error) {
        console.error('Error updating poll votes:', error);
        throw error;
      }
      
      // Handle case where update returns 0 rows (RLS policy blocking or poll not found)
      if (!data || data.length === 0) {
        const errorMsg = 'Failed to update poll vote. You may not have permission to vote on this poll, or the poll may not exist.';
        console.error(errorMsg, { pollId, userId: user.id, userEmail: user.email });
        throw new Error(errorMsg);
      }
      
      // Parse target_friend_emails if it's a JSON string
      const result = data[0];
      if (result && result.target_friend_emails && typeof result.target_friend_emails === 'string') {
        try {
          result.target_friend_emails = JSON.parse(result.target_friend_emails);
        } catch (e) {
          console.warn('Failed to parse target_friend_emails in vote result:', e);
        }
      }
      
      return result
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async delete(id) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Verify user owns the poll before deleting
      const { data: poll, error: fetchError } = await supabase
        .from('socialpoll')
        .select('created_by')
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError
      if (!poll) throw new Error('Poll not found')

      // Check ownership - created_by can be email or id
      const isOwner = poll.created_by === user.email || 
                     poll.created_by === user.id
      
      if (!isOwner) {
        throw new Error('You can only delete polls you created')
      }

      const { error } = await supabase
        .from('socialpoll')
        .delete()
        .eq('id', id)

      if (error) throw error
      return true
    } catch (error) {
      handleSupabaseError(error)
      throw error
    }
  },

  async getAnalytics(pollId) {
    try {
      const poll = await this.getById(pollId)
      if (!poll) throw new Error('Poll not found')

      const votes = Array.isArray(poll.votes) ? poll.votes.filter(v => !v?._metadata) : []
      const totalResponses = votes.reduce((sum, vote) => sum + (vote.count || 0), 0)
      const uniqueVoters = new Set()

      votes.forEach((vote) => {
        ;(vote.voters || []).forEach((voter) => {
          if (voter) uniqueVoters.add(voter)
        })
      })

      const totalRecipients = Array.isArray(poll.target_friend_emails)
        ? poll.target_friend_emails.length
        : 0

      const distribution = votes.map((vote) => ({
        option: vote.option,
        count: vote.count || 0,
        percentage: totalResponses > 0 ? ((vote.count || 0) / totalResponses) * 100 : 0
      }))

      return {
        poll_id: pollId,
        total_recipients: totalRecipients,
        total_responses: uniqueVoters.size,
        response_rate: totalRecipients > 0
          ? (uniqueVoters.size / totalRecipients) * 100
          : 0,
        pending_responses: Math.max(totalRecipients - uniqueVoters.size, 0),
        vote_distribution: distribution,
        reminder_count: poll.reminder_count || 0,
        link_clicks: poll.link_clicks || 0,
        average_response_time: poll.average_response_time || null,
        calculated_at: new Date().toISOString(),
        response_timeline: await this.getResponseTimeline(pollId)
      }
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async getResponseTimeline(_pollId) {
    // Placeholder implementation until vote_events table is populated
    return []
  },

  // ============================================================================
  // ENHANCED POLL FUNCTIONALITY
  // ============================================================================

  async createEnhancedPoll(pollData) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const enhancedPollData = {
        ...pollData,
        poll_type: pollData.poll_type || 'basic',
        poll_data: pollData.poll_data || {},
        group_id: pollData.group_id || null,
        user_id: user.id,
        created_by: user.email || user.id
      };

      const { data, error } = await supabase
        .from('socialpoll')
        .insert([enhancedPollData])
        .select(`
          *,
          group:friend_groups!group_id(id, name, color)
        `)
        .single();

      if (error) throw error;

      // Record activity if this is a group poll (handled by trigger, but manual backup)
      if (data.group_id) {
        try {
          await this.recordActivity(data.group_id, data.user_id, 'poll_created', {
            poll_id: data.id,
            poll_question: data.question,
            poll_type: data.poll_type
          });
        } catch (activityError) {
          console.warn('Activity recording failed:', activityError);
        }
      }

      return data;
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async getGroupPolls(groupId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('socialpoll')
        .select(`
          *,
          camp(*)
        `)
        .eq('group_id', groupId)
        .order('created_date', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async getPollsByType(pollType, groupId = null) {
    try {
      let query = supabase
        .from('socialpoll')
        .select(`
          *,
          camp(*),
          group:friend_groups!group_id(id, name, color)
        `)
        .eq('poll_type', pollType);

      if (groupId) {
        query = query.eq('group_id', groupId);
      }

      const { data, error } = await query
        .order('created_date', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  // Enhanced vote with activity tracking
  async voteWithActivity(pollId, voteData, groupId = null) {
    try {
      const result = await this.vote(pollId, voteData);
      
      // Additional activity recording for enhanced polls
      if (groupId) {
        const user = await getCurrentUser();
        await this.recordActivity(groupId, user.id, 'poll_voted', {
          poll_id: pollId,
          vote_data: voteData,
          poll_question: result.question
        });
      }

      return result;
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  // Record activity helper
  async recordActivity(groupId, userId, activityType, activityData) {
    try {
      const { error } = await supabase
        .rpc('record_group_activity', {
          p_group_id: groupId,
          p_user_id: userId,
          p_activity_type: activityType,
          p_activity_data: activityData,
          p_related_id: activityData.poll_id || null,
          p_related_table: 'socialpoll'
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.warn('Activity recording failed:', error);
      // Don't throw error for activity recording failures
      return false;
    }
  },

  // Get poll templates for quick creation
  getPollTemplates() {
    return [
      {
        id: 'soccer_camp_interest',
        title: 'Soccer Camp Interest',
        description: 'Which soccer camp week works for your family?',
        poll_type: 'date_selection',
        question: 'Which soccer camp week works best for your family?',
        options: [
          { text: 'Week 1 (June 5-9)', votes: 0 },
          { text: 'Week 2 (June 12-16)', votes: 0 },
          { text: 'Week 3 (June 19-23)', votes: 0 },
          { text: 'Week 4 (June 26-30)', votes: 0 }
        ],
        poll_data: {
          dateRange: true,
          allowMultiple: false
        },
        iconEmoji: '⚽',
        iconColor: 'bg-green-500'
      },
      {
        id: 'art_camp_timing',
        title: 'Art Camp Timing',
        description: 'When should we do art camp together?',
        poll_type: 'date_selection',
        question: 'What time works best for art camp?',
        options: [
          { text: 'Morning (9am-12pm)', votes: 0 },
          { text: 'Afternoon (1pm-4pm)', votes: 0 },
          { text: 'Full Day (9am-4pm)', votes: 0 }
        ],
        poll_data: {
          timeSelection: true,
          duration: true
        },
        iconEmoji: '🎨',
        iconColor: 'bg-purple-500'
      },
      {
        id: 'budget_planning',
        title: 'Budget Planning',
        description: "What's your budget range for summer camps?",
        poll_type: 'budget_range',
        question: 'What\'s your preferred budget range per week?',
        options: [
          { text: '$100-200/week', votes: 0 },
          { text: '$200-300/week', votes: 0 },
          { text: '$300-400/week', votes: 0 },
          { text: '$400+/week', votes: 0 }
        ],
        poll_data: {
          budgetSlider: { min: 100, max: 500, step: 50 },
          anonymous: true
        },
        iconEmoji: '💰',
        iconColor: 'bg-blue-500'
      }
    ];
  }
};

// FriendRequest entity - handles friend invitations
export const FriendRequest = {
  async sendRequest(requestData) {
    try {
      console.log('FriendRequest.sendRequest - Starting with requestData:', requestData)
      
      // Get current user using cached helper (fast, minimal network calls)
      const user = await getCurrentUser()
      
      console.log('FriendRequest.sendRequest - User ID:', user.id)
      console.log('FriendRequest.sendRequest - User email:', user.email)

      // Ensure user has a valid ID
      if (!user.id) {
        console.error('User ID is missing:', user)
        throw new Error('User ID is missing. Please try logging out and back in.')
      }

      // Double-check that the user ID is a valid UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
      if (!uuidRegex.test(user.id)) {
        console.error('User ID is not a valid UUID:', user.id)
        throw new Error('Invalid user ID format. Please try logging out and back in.')
      }
      
      // If user.id is an email, we need to get the actual UUID
      if (user.id && user.id.includes('@')) {
        console.error('User ID is an email address, not a UUID:', user.id)
        console.error('This suggests an authentication issue. Please check your Supabase configuration.')
        throw new Error('Authentication error: User ID is not a valid UUID. Please log out and log back in.')
      }

      // Profile check is optional for friend requests - we only need the user ID
      // Simplified: profile will be created by database trigger if it doesn't exist

      // Final validation before database query
      if (user.id && user.id.includes('@')) {
        console.error('CRITICAL ERROR: user.id is still an email address:', user.id)
        console.error('This indicates a fundamental authentication issue')
        throw new Error('Authentication error: User ID is an email address. Please log out and log back in.')
      }

      // Validate that we have either email or phone
      if (!requestData.recipient_email && !requestData.recipient_phone) {
        throw new Error('Either an email address or phone number is required.')
      }

      // Normalize recipient email for consistent storage and comparison
      if (requestData.recipient_email) {
        requestData.recipient_email = requestData.recipient_email.toLowerCase().trim()
      }
      if (requestData.recipient_phone) {
        requestData.recipient_phone = requestData.recipient_phone.trim()
      }

      // Check for existing requests in both directions
      // Note: We can only check for requests where the current user is the sender
      // We cannot check for requests where the invitee is the sender because they don't have an account yet
      let existingRequests = []
      let queryError = null

      // Check by email if provided
      if (requestData.recipient_email) {
        const { data, error } = await supabase
          .from('friend_requests')
          .select('id, status, sender_id, recipient_email, recipient_phone')
          .eq('sender_id', user.id)
          .ilike('recipient_email', requestData.recipient_email)

        if (error) queryError = error
        else existingRequests = data || []
      }

      // Also check by phone if provided
      if (requestData.recipient_phone && !queryError) {
        const { data, error } = await supabase
          .from('friend_requests')
          .select('id, status, sender_id, recipient_email, recipient_phone')
          .eq('sender_id', user.id)
          .eq('recipient_phone', requestData.recipient_phone)

        if (error) {
          queryError = error
        } else if (data && data.length > 0) {
          // Merge with existing results, avoiding duplicates
          const existingIds = new Set(existingRequests.map(r => r.id))
          existingRequests = [...existingRequests, ...data.filter(r => !existingIds.has(r.id))]
        }
      }

      if (queryError) throw queryError

      // Check for existing requests from sender to recipient (by email or phone)
      const sentRequest = existingRequests?.find(req => {
        if (requestData.recipient_email && req.recipient_email) {
          return req.sender_id === user.id && req.recipient_email.toLowerCase() === requestData.recipient_email.toLowerCase()
        }
        if (requestData.recipient_phone && req.recipient_phone) {
          return req.sender_id === user.id && req.recipient_phone === requestData.recipient_phone
        }
        return false
      })

      // Handle sent request
      if (sentRequest) {
        if (sentRequest.status === 'pending') {
          throw new Error('You already have a pending request to this person.')
        } else if (sentRequest.status === 'accepted') {
          throw new Error('You are already friends with this person.')
        } else {
          // If declined or cancelled, update the existing request instead of creating new
          const updateData = {
            recipient_name: requestData.recipient_name,
            recipient_photo: requestData.recipient_photo || null,
            message: requestData.message,
            status: 'pending',
            updated_at: new Date().toISOString()
          }
          
          // Update recipient_phone if provided
          if (requestData.recipient_phone) {
            updateData.recipient_phone = requestData.recipient_phone
          }
          
          const { data, error } = await supabase
            .from('friend_requests')
            .update(updateData)
            .eq('id', sentRequest.id)
            .select()
            .single()
          
          if (error) throw error
          return data
        }
      }

      // Note: We cannot check for requests from the invitee to the current user
      // because the invitee doesn't have an account yet (they're being invited for the first time)

      // For phone-only invites, we need a placeholder email since recipient_email is NOT NULL
      // We'll use a format that won't conflict with real emails: phone-{phone}@sms-invite.summer-squad.local
      let recipientEmail = requestData.recipient_email
      if (!recipientEmail && requestData.recipient_phone) {
        // Create a unique placeholder email for phone-only invites
        const sanitizedPhone = requestData.recipient_phone.replace(/\D/g, '') // Remove non-digits
        recipientEmail = `phone-${sanitizedPhone}@sms-invite.summer-squad.local`
      }

      const insertData = {
        sender_id: user.id,
        recipient_email: recipientEmail,
        recipient_name: requestData.recipient_name,
        recipient_photo: requestData.recipient_photo || null,
        recipient_phone: requestData.recipient_phone || null,
        message: requestData.message,
        status: 'pending'
      }

      const { data, error } = await supabase
        .from('friend_requests')
        .insert([insertData])
        .select()
        .single()
      
      if (error) throw error

      // IMPORTANT: Return immediately after successful DB insert
      // Email/SMS sending is fire-and-forget and should not block the response
      const returnData = data

      // Send friend invitation email or SMS asynchronously (non-blocking)
      // Use setTimeout to ensure this runs after the function returns
      if (returnData) {
        setTimeout(async () => {
          // Send email if we have a real email address (non-blocking)
          if (requestData.recipient_email && !requestData.recipient_email.includes('@sms-invite.summer-squad.local')) {
            // Fire-and-forget email sending with timeout protection
            Promise.race([
              (async () => {
                try {
                  // Get sender's profile information
                  const { data: senderProfile } = await supabase
                    .from('profiles')
                    .select('full_name, email')
                    .eq('id', user.id)
                    .single()

                  const senderName = senderProfile?.full_name || user.email?.split('@')[0] || 'Someone'
                  const senderEmail = user.email

                  // Get app URL from environment or use default
                  const appUrl = window.location.origin
                  const inviteUrl = `${appUrl}/auth/signup?invite=${returnData.id}`

                  // Import SendEmail dynamically to avoid circular dependencies
                  const { SendEmail } = await import('./integrations')
                  
                  await SendEmail({
                    to: requestData.recipient_email,
                    template: 'friend-invitation',
                    data: {
                      senderName,
                      senderEmail,
                      recipientName: requestData.recipient_name || requestData.recipient_email.split('@')[0],
                      message: requestData.message || `Hi! I'd like to connect with you on Summer Squad to coordinate camp planning together.`,
                      inviteUrl,
                      appUrl
                    }
                  })
                  
                  console.log('Friend invitation email sent successfully')
                } catch (emailError) {
                  // Log error but don't fail the request
                  console.warn('Failed to send friend invitation email:', emailError.message)
                  // Request was created successfully, email failure is non-critical
                }
              })(),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Email sending timeout')), 10000)
              )
            ]).catch(err => {
              console.warn('Email sending error or timeout:', err.message)
            })
          }
          
          // Send SMS if we have a phone number (and either no email or placeholder email)
          if (requestData.recipient_phone) {
            try {
              // Get sender's profile information
              const { data: senderProfile } = await supabase
                .from('profiles')
                .select('full_name, email')
                .eq('id', user.id)
                .single()

              const senderName = senderProfile?.full_name || user.email?.split('@')[0] || 'Someone'
              const appUrl = window.location.origin
              const inviteUrl = `${appUrl}/auth/signup?invite=${returnData.id}`
              
              // Build SMS message
              const message = requestData.message || `Hi! I'd like to connect with you on Summer Squad to coordinate camp planning together.`
              const smsBody = `${message}\n\nJoin me: ${inviteUrl}\n\n- ${senderName}`
              
              // Open SMS app with pre-filled message
              const smsUrl = `sms:${requestData.recipient_phone.replace(/\D/g, '')}?body=${encodeURIComponent(smsBody)}`
              
              // Open SMS app in a new window/tab (works on mobile devices)
              if (typeof window !== 'undefined') {
                window.location.href = smsUrl
                console.log('SMS app opened with pre-filled message')
              }
            } catch (smsError) {
              // Log error but don't fail the request
              console.warn('Failed to open SMS app:', smsError.message)
              // Request was created successfully, SMS opening failure is non-critical
            }
          }
        }, 0)
      }

      // Return immediately - don't wait for email/SMS
      return returnData
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async getIncomingRequests() {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      console.log('FriendRequest.getIncomingRequests - User email:', user.email);

      // Normalize email for comparison (lowercase, trim whitespace)
      const normalizedEmail = user.email?.toLowerCase().trim()

      const { data, error } = await supabase
        .from('friend_requests')
        .select(`
          *,
          sender:profiles!friend_requests_sender_id_fkey(*)
        `)
        .ilike('recipient_email', normalizedEmail) // Case-insensitive match
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
      
      console.log('FriendRequest.getIncomingRequests - Query result:', { data, error });
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('FriendRequest.getIncomingRequests - Error:', error);
      handleSupabaseError(error)
    }
  },

  async getSentRequests() {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      console.log('FriendRequest.getSentRequests - User ID:', user.id);

      const { data, error } = await supabase
        .from('friend_requests')
        .select('*')
        .eq('sender_id', user.id)
        .order('created_at', { ascending: false })
      
      console.log('FriendRequest.getSentRequests - Query result:', { data, error });
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('FriendRequest.getSentRequests - Error:', error);
      handleSupabaseError(error)
    }
  },

  async acceptRequest(requestId) {
    try {
      // Try using the RPC function first (preferred method)
      const { data, error } = await supabase.rpc('accept_friend_request', {
        request_id: requestId
      })
      
      // If RPC succeeds, return the result
      if (!error) {
        console.log('Friend request accepted via RPC:', data)
        return data
      }
      
      // If RPC function doesn't exist, fall back to client-side implementation
      if (error && error.message?.includes('function') && error.message?.includes('does not exist')) {
        console.warn('RPC function not found, using client-side fallback')
        return await this.acceptRequestClientSide(requestId)
      }
      
      // For other errors, throw them
      throw error
    } catch (error) {
      console.error('Error in acceptRequest:', error)
      handleSupabaseError(error)
    }
  },

  // Client-side fallback for accepting friend requests
  async acceptRequestClientSide(requestId) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      console.log('Accepting friend request client-side:', requestId)

      // Get the friend request with sender profile
      const { data: request, error: requestError } = await supabase
        .from('friend_requests')
        .select('*, sender:profiles!friend_requests_sender_id_fkey(*)')
        .eq('id', requestId)
        .eq('status', 'pending')
        .single()
      
      if (requestError) throw requestError
      if (!request) throw new Error('Friend request not found or already processed')
      
      console.log('Friend request details:', request)
      
      // Verify current user is the recipient
      if (user.email.toLowerCase() !== request.recipient_email.toLowerCase()) {
        throw new Error('Only the recipient can accept this request')
      }
      
      // Update request status
      const { error: updateError } = await supabase
        .from('friend_requests')
        .update({ status: 'accepted', updated_at: new Date().toISOString() })
        .eq('id', requestId)
      
      if (updateError) {
        console.error('Error updating request:', updateError)
        throw updateError
      }
      
      console.log('Friend request status updated to accepted')
      
      // Create connection from sender to recipient
      const connection1 = {
        user_id: request.sender_id,
        friend_id: user.id,
        friend_email: user.email.toLowerCase(),
        friend_name: user.full_name || user.email,
        status: 'accepted'
      }
      
      console.log('Creating connection 1 (sender → recipient):', connection1)
      
      const { error: conn1Error } = await supabase
        .from('friendconnection')
        .upsert(connection1, {
          onConflict: 'user_id,friend_email'
        })
      
      if (conn1Error) {
        console.error('Error creating connection 1:', conn1Error)
        // Don't throw, continue to try second connection
      } else {
        console.log('Connection 1 created successfully')
      }
      
      // Create connection from recipient to sender
      const connection2 = {
        user_id: user.id,
        friend_id: request.sender_id,
        friend_email: request.sender.email.toLowerCase(),
        friend_name: request.sender.full_name || request.sender.email,
        status: 'accepted'
      }
      
      console.log('Creating connection 2 (recipient → sender):', connection2)
      
      const { error: conn2Error } = await supabase
        .from('friendconnection')
        .upsert(connection2, {
          onConflict: 'user_id,friend_email'
        })
      
      if (conn2Error) {
        console.error('Error creating connection 2:', conn2Error)
        // Don't throw, at least one connection might have worked
      } else {
        console.log('Connection 2 created successfully')
      }
      
      console.log('Friend request accepted successfully (client-side)')
      
      return { 
        success: true, 
        requestId, 
        message: 'Friend request accepted successfully',
        method: 'client-side'
      }
    } catch (error) {
      console.error('Error in acceptRequestClientSide:', error)
      throw error
    }
  },

  async declineRequest(requestId) {
    try {
      const { data, error } = await supabase
        .from('friend_requests')
        .update({ status: 'declined' })
        .eq('id', requestId)
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async cancelRequest(requestId) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('friend_requests')
        .update({ status: 'cancelled' })
        .eq('id', requestId)
        .eq('sender_id', user.id)
        .eq('status', 'pending')
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  }
};

// FriendConnection entity - handles accepted friendships
export const FriendConnection = {
  async list() {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('friendconnection')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'accepted')
      
      if (error) throw error
      return data || []
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async removeFriend(connectionId) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('friendconnection')
        .update({ status: 'declined' })
        .eq('id', connectionId)
        .eq('user_id', user.id)
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async blockFriend(connectionId) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('friendconnection')
        .update({ status: 'declined' })
        .eq('id', connectionId)
        .eq('user_id', user.id)
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async updateStatus(connectionId, status) {
    try {
      const { data, error } = await supabase
        .from('friendconnection')
        .update({ status })
        .eq('id', connectionId)
        .select()
        .single()
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  }
};

// FriendGroup entity - handles friend groups
export const FriendGroup = {
  async create(name, description = '', color = '#8B5CF6') {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const trimmedName = name.trim()

      // Check if a group with this name already exists
      const { data: existingGroup, error: checkError } = await supabase
        .from('friend_groups')
        .select('id, name')
        .eq('user_id', user.id)
        .eq('name', trimmedName)
        .maybeSingle()

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw checkError
      }

      if (existingGroup) {
        throw new Error(`A group with the name "${trimmedName}" already exists. Please choose a different name.`)
      }

      const { data, error } = await supabase
        .from('friend_groups')
        .insert({
          user_id: user.id,
          name: trimmedName,
          description: description.trim(),
          color
        })
        .select()
        .single()

      if (error) {
        // Handle duplicate key constraint error specifically
        if (error.code === '23505' || error.message?.includes('duplicate key')) {
          throw new Error(`A group with the name "${trimmedName}" already exists. Please choose a different name.`)
        }
        throw error
      }
      return data
    } catch (error) {
      // If it's already a user-friendly error, re-throw it
      if (error.message && error.message.includes('already exists')) {
        throw error
      }
      handleSupabaseError(error)
    }
  },

  async list() {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('friend_groups')
        .select(`
          *,
          friend_group_members(
            id,
            friend_connection_id,
            friendconnection(
              id,
              friend_name,
              friend_email,
              friend_id
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async update(groupId, updates) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('friend_groups')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', groupId)
        .eq('user_id', user.id) // Ensure user can only update their own groups
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async delete(groupId) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // First, verify the group belongs to the user
      const { data: group, error: groupError } = await supabase
        .from('friend_groups')
        .select('id')
        .eq('id', groupId)
        .eq('user_id', user.id)
        .single()

      if (groupError || !group) {
        throw new Error('Group not found or not authorized')
      }

      // Delete all group members first to avoid foreign key constraint issues
      const { error: membersError } = await supabase
        .from('friend_group_members')
        .delete()
        .eq('group_id', groupId)

      if (membersError) {
        console.error('Error deleting group members:', membersError)
        throw membersError
      }

      // Now delete the group itself
      const { error } = await supabase
        .from('friend_groups')
        .delete()
        .eq('id', groupId)
        .eq('user_id', user.id) // Ensure user can only delete their own groups

      if (error) throw error
      return true
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async addMember(groupId, friendConnectionId) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Verify the group belongs to the user
      const { data: group, error: groupError } = await supabase
        .from('friend_groups')
        .select('id')
        .eq('id', groupId)
        .eq('user_id', user.id)
        .single()

      if (groupError || !group) {
        throw new Error('Group not found or not authorized')
      }

      // Verify the friend connection belongs to the user
      const { data: friend, error: friendError } = await supabase
        .from('friendconnection')
        .select('id')
        .eq('id', friendConnectionId)
        .eq('user_id', user.id)
        .single()

      if (friendError || !friend) {
        throw new Error('Friend connection not found or not authorized')
      }

      const { data, error } = await supabase
        .from('friend_group_members')
        .insert({
          group_id: groupId,
          friend_connection_id: friendConnectionId
        })
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async removeMember(groupId, friendConnectionId) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Verify the group belongs to the user
      const { data: group, error: groupError } = await supabase
        .from('friend_groups')
        .select('id')
        .eq('id', groupId)
        .eq('user_id', user.id)
        .single()

      if (groupError || !group) {
        throw new Error('Group not found or not authorized')
      }

      const { error } = await supabase
        .from('friend_group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('friend_connection_id', friendConnectionId)

      if (error) throw error
      return true
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async getMembers(groupId) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Verify the group belongs to the user
      const { data: group, error: groupError } = await supabase
        .from('friend_groups')
        .select('id')
        .eq('id', groupId)
        .eq('user_id', user.id)
        .single()

      if (groupError || !group) {
        throw new Error('Group not found or not authorized')
      }

      const { data, error } = await supabase
        .from('friend_group_members')
        .select(`
          id,
          friend_connection_id,
          added_at,
          friendconnection(
            id,
            friend_name,
            friend_email,
            friend_id,
            children,
            status
          )
        `)
        .eq('group_id', groupId)
        .order('added_at', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  // ============================================================================
  // ENHANCED PLANNING GROUP FUNCTIONALITY
  // ============================================================================

  async getSavedCamps(groupId) {
    try {
      // Get saved camps without the problematic join
      const { data, error } = await supabase
        .from('group_saved_camps')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // If we have saved_by user IDs, optionally fetch profile data separately
      // For now, just return the data as-is since the component can handle missing profile data
      return data || [];
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async saveCamp(groupId, campData) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('group_saved_camps')
        .insert([{
          group_id: groupId,
          saved_by: user.id,
          camp_name: campData.name,
          camp_url: campData.url,
          camp_location: campData.location,
          camp_price: campData.price,
          camp_dates: campData.dates,
          notes: campData.notes,
          tags: campData.tags || []
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async reactToCamp(campId, reactionType) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .rpc('update_camp_reactions', {
          p_camp_id: campId,
          p_user_id: user.id,
          p_reaction_type: reactionType
        });

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async getRecentActivity(groupId, limit = 10) {
    try {
      // Get activity without the problematic join to auth.users
      // The user_id field can be used to fetch user data separately if needed
      const { data, error } = await supabase
        .from('group_activities')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async getCarpoolOpportunities(groupId) {
    try {
      // Get carpool data without the problematic join to auth.users
      // The coordinator_user_id field can be used to fetch user data separately if needed
      const { data, error } = await supabase
        .from('carpool_coordination')
        .select('*')
        .eq('group_id', groupId)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async createCarpool(groupId, carpoolData) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('carpool_coordination')
        .insert([{
          group_id: groupId,
          coordinator_user_id: user.id,
          event_name: carpoolData.eventName,
          event_location: carpoolData.eventLocation,
          event_dates: carpoolData.eventDates,
          pickup_location: carpoolData.pickupLocation,
          dropoff_location: carpoolData.dropoffLocation,
          departure_time: carpoolData.departureTime,
          return_time: carpoolData.returnTime,
          driver_capacity: carpoolData.driverCapacity || 4,
          notes: carpoolData.notes
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  // Get enhanced group data with all related information
  async getEnhancedGroupData(groupId) {
    try {
      const [groupResult, savedCamps, recentActivity, carpools] = await Promise.all([
        supabase.from('friend_groups').select('*').eq('id', groupId).single(),
        this.getSavedCamps(groupId),
        this.getRecentActivity(groupId, 5),
        this.getCarpoolOpportunities(groupId)
      ]);

      if (groupResult.error) throw groupResult.error;

      return {
        ...groupResult.data,
        savedCamps: savedCamps || [],
        recentActivity: recentActivity || [],
        carpools: carpools || []
      };
    } catch (error) {
      handleSupabaseError(error)
    }
  }
};

// UserProgram entity - handles user's saved programs
export const UserProgram = {
  async get(id) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('user_programs')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id) // Ensure user can only access their own programs
        .maybeSingle()

      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async list() {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('user_programs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async create(programData) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Get user email from profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', user.id)
        .maybeSingle()

      const userEmail = profile?.email || user.email

      // Extract email_scout_id from various possible locations
      let emailScoutId = null;
      if (programData.email_scout_id) {
        emailScoutId = programData.email_scout_id;
      } else if (programData.metadata?.email_scout_id) {
        emailScoutId = programData.metadata.email_scout_id;
      } else if (programData.program_data?.email_scout_id) {
        emailScoutId = programData.program_data.email_scout_id;
      } else if (programData.program_data?.email_source?.email_id) {
        emailScoutId = programData.program_data.email_source.email_id;
      }

      const insertData = {
        user_id: user.id,
        user_email: userEmail,
        program_type: programData.program_type || 'camp',
        program_data: programData.program_data || {},
        status: programData.status || 'considering',
        year_tracked: programData.year_tracked || new Date().getFullYear(),
        child_names: programData.child_names || [],
        email_scout_id: emailScoutId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('user_programs')
        .insert(insertData)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async update(id, updates) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Prepare update data, excluding user_id (should not be changed)
      const updateData = {
        ...updates,
        updated_at: new Date().toISOString()
      }
      delete updateData.user_id // Prevent changing user_id

      const { data, error } = await supabase
        .from('user_programs')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id) // Ensure user can only update their own programs
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async filter(filters = {}) {
    let user;
    try {
      user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      let query = supabase
        .from('user_programs')
        .select('*')
        .eq('user_id', user.id)

      // Apply filters using SQL queries for better performance
      Object.entries(filters).forEach(([key, value]) => {
        if (key === 'user_email') {
          // Skip user_email filter since we already filter by user_id
          // This maintains compatibility with existing calls
          return;
        }

        if (key === 'status') {
          // Status filtering will be done in memory after query
          // to handle both status column and program_data.selected_statuses
          // We'll filter this after the query
          return;
        }

        if (key.startsWith('program_data.')) {
          // Handle JSONB queries for program_data fields
          const jsonbField = key.split('.')[1]; // Extract field name after 'program_data.'
          // Use ->> for text extraction (field name should NOT be quoted in PostgREST)
          query = query.eq(`program_data->>${jsonbField}`, value);
          return;
        }

        // Handle regular column filters
        if (value === null || value === undefined) {
          query = query.is(key, null);
        } else {
          query = query.eq(key, value);
        }
      });

      // Order by created_at descending
      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      // For status filter, we need to do additional filtering in memory for complex cases
      // (e.g., checking selected_statuses array)
      if (filters.status) {
        const filterValues = Array.isArray(filters.status) ? filters.status : [filters.status];
        return (data || []).filter(program => {
          const statusList = Array.isArray(program.status)
            ? program.status
            : program.status
              ? [program.status]
              : [];
          const selectedStatuses = Array.isArray(program.program_data?.selected_statuses)
            ? program.program_data.selected_statuses
            : [];

          const allStatuses = [...statusList, ...selectedStatuses].map(status =>
            String(status || '').toLowerCase()
          );

          if (allStatuses.length === 0) {
            return false;
          }

          return filterValues.some(filterValue => {
            const normalizedFilter = String(filterValue || '').toLowerCase();
            return allStatuses.some(status => {
              if (status === normalizedFilter) return true;
              if (normalizedFilter === 'favorite') {
                return status.startsWith('favorite');
              }
              return false;
            });
          });
        });
      }

      return data || []
    } catch (error) {
      // Handle errors gracefully - return empty array if there are issues
      console.error('Error filtering user programs:', error);
      // Fallback to helper function if table doesn't exist yet (backward compatibility)
      if (!user) {
        return [];
      }
      try {
        const programs = await getTrackedPrograms(user.id);
        // Apply filters in memory as fallback
        // ... (keep existing filter logic as fallback)
        return programs || [];
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        return [];
      }
    }
  },

  async removeFromTimeline(id) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Get current program
      const { data: program, error: fetchError } = await supabase
        .from('user_programs')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id) // Ensure user can only access their own programs
        .maybeSingle()

      if (fetchError) throw fetchError
      if (!program) {
        // Program doesn't exist or was already deleted, return null gracefully
        console.warn(`Program ${id} not found or already deleted`)
        return null
      }

      // Remove 'booked' and 'considering' from status array/string
      let updatedStatus = program.status
      if (Array.isArray(program.status)) {
        updatedStatus = program.status.filter(s => 
          s !== 'booked' && s !== 'considering' && s !== 'confirmed'
        )
        // If status becomes empty, set to 'saved' to keep in saved programs
        if (updatedStatus.length === 0) {
          updatedStatus = ['saved']
        }
      } else if (typeof program.status === 'string') {
        // If status is a single value and it's booked/considering, change to 'saved'
        if (program.status === 'booked' || program.status === 'considering' || program.status === 'confirmed') {
          updatedStatus = 'saved'
        }
      }

      // Remove 'booked' and 'considering' from program_data.selected_statuses
      const programData = program.program_data || {}
      let updatedSelectedStatuses = []
      if (Array.isArray(programData.selected_statuses)) {
        updatedSelectedStatuses = programData.selected_statuses.filter(s =>
          s !== 'booked' && s !== 'considering' && s !== 'confirmed'
        )
      }

      // Update the program
      const updateData = {
        status: updatedStatus,
        program_data: {
          ...programData,
          selected_statuses: updatedSelectedStatuses
        },
        updated_at: new Date().toISOString()
      }

      const { data: updatedProgram, error: updateError } = await supabase
        .from('user_programs')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .maybeSingle()

      if (updateError) throw updateError
      
      // If program doesn't exist (was already deleted), return null gracefully
      if (!updatedProgram) {
        console.warn(`Program ${id} not found or already deleted`)
        return null
      }
      
      return updatedProgram
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async delete(id) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { error } = await supabase
        .from('user_programs')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id) // Ensure user can only delete their own programs

      if (error) throw error
      return true
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  /**
   * Get all sessions for a base camp
   * @param {string} userId - User ID
   * @param {string} baseCampId - Base camp ID (parent_camp_id = NULL) or camp_id from user_camp_sessions
   * @returns {Promise<Array>} Array of session records
   */
  async getSessionsForCamp(userId, baseCampId) {
    try {
      const allSessions = [];
      
      // Load from user_programs (legacy sessions)
      const { data: userProgramSessions, error: userProgramError } = await supabase
        .from('user_programs')
        .select('*')
        .eq('user_id', userId)
        .eq('parent_camp_id', baseCampId)
        .order('created_at', { ascending: true });

      if (userProgramError) throw userProgramError;
      if (userProgramSessions) allSessions.push(...userProgramSessions);

      // Also load from user_camp_sessions (optimized sessions)
      // camp_id can be user_programs.id or camp_audit_data.place_id/id
      const { data: optimizedSessions, error: optimizedError } = await supabase
        .from('user_camp_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('camp_id', baseCampId)
        .order('user_camp_start_date', { ascending: true, nullsFirst: false });

      if (optimizedError) {
        console.warn('Error loading optimized sessions from user_camp_sessions:', optimizedError);
      } else if (optimizedSessions && optimizedSessions.length > 0) {
        // Transform user_camp_sessions format to match user_programs format
        const transformed = optimizedSessions.map(s => ({
          id: s.id,
          user_id: s.user_id,
          parent_camp_id: baseCampId, // Link to parent
          program_data: {
            session: s.session_name,
            session_name: s.session_name,
            session_year: s.session_year,
            camp_start_date: s.user_camp_start_date,
            camp_end_date: s.user_camp_end_date,
            start_date: s.user_camp_start_date,
            end_date: s.user_camp_end_date,
            price: s.cost,
            session_price: s.cost,
          },
          session: s.session_name,
          session_name: s.session_name,
          session_year: s.session_year,
          user_camp_start_date: s.user_camp_start_date,
          user_camp_end_date: s.user_camp_end_date,
          start_date: s.user_camp_start_date,
          end_date: s.user_camp_end_date,
          price: s.cost,
          session_price: s.cost,
          cost: s.cost,
          created_at: s.created_at,
          updated_at: s.updated_at,
          from_user_camp_sessions: true // Flag to identify source
        }));
        allSessions.push(...transformed);
      }

      // Deduplicate by session name + year
      const unique = Array.from(
        new Map(allSessions.map(s => {
          const sessionName = s.session || s.program_data?.session || s.session_name || '';
          const sessionYear = s.session_year || s.program_data?.session_year || '';
          return [`${sessionName}-${sessionYear}`, s];
        })).values()
      );

      return unique;
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  /**
   * Get the base camp for a session
   * @param {string} sessionId - Session record ID
   * @returns {Promise<Object|null>} Base camp record or null
   */
  async getBaseCampForSession(sessionId) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // First get the session to find its parent_camp_id
      const { data: session, error: sessionError } = await supabase
        .from('user_programs')
        .select('parent_camp_id')
        .eq('id', sessionId)
        .eq('user_id', user.id)
        .maybeSingle()

      if (sessionError) throw sessionError
      if (!session || !session.parent_camp_id) {
        // This is a base camp, not a session
        return null
      }

      // Get the base camp
      const { data: baseCamp, error: baseCampError } = await supabase
        .from('user_programs')
        .select('*')
        .eq('id', session.parent_camp_id)
        .eq('user_id', user.id)
        .maybeSingle()

      if (baseCampError) throw baseCampError
      return baseCamp
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  /**
   * Create a new session linked to a base camp
   * @param {string} baseCampId - Base camp ID
   * @param {Object} sessionData - Session data (program_data, status, child_names, etc.)
   * @returns {Promise<Object>} Created session record
   */
  async createSession(baseCampId, sessionData) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      // Get base camp to copy base data
      const { data: baseCamp, error: baseCampError } = await supabase
        .from('user_programs')
        .select('*')
        .eq('id', baseCampId)
        .eq('user_id', user.id)
        .maybeSingle()

      if (baseCampError) throw baseCampError
      if (!baseCamp) throw new Error('Base camp not found')

      // Get user email from profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', user.id)
        .maybeSingle()

      const userEmail = profile?.email || user.email

      // Merge base camp data with session-specific data
      const baseProgramData = baseCamp.program_data || {}
      const sessionProgramData = sessionData.program_data || {}

      const insertData = {
        user_id: user.id,
        user_email: userEmail,
        parent_camp_id: baseCampId,
        program_type: sessionData.program_type || baseCamp.program_type || 'camp',
        program_data: {
          // Copy base camp data
          name: baseProgramData.name || baseCamp.name,
          organization: baseProgramData.organization || baseCamp.organization,
          location: baseProgramData.location || baseProgramData.general_location || baseCamp.location,
          // Override with session-specific data
          ...sessionProgramData,
          // Ensure session fields are set (session is in program_data, not at top level)
          session: sessionProgramData.session || null,
          session_year: sessionProgramData.session_year || null,
          camp_start_date: sessionProgramData.camp_start_date || null,
          camp_end_date: sessionProgramData.camp_end_date || null,
          price: sessionProgramData.price || null, // Session-specific price
        },
        status: sessionData.status || baseCamp.status || 'considering',
        year_tracked: sessionData.year_tracked || baseCamp.year_tracked || new Date().getFullYear(),
        child_names: sessionData.child_names || baseCamp.child_names || [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('user_programs')
        .insert(insertData)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  }
};

// User entity - handles authentication and user data
export const User = {
  async syncProfileWithAuth() {
    try {
      // Get cached user data from auth
      const user = await getCachedUser(supabase)
      if (!user) return null

      console.log('Syncing profile with auth data for user:', user.id)

      // Prepare profile data from auth
      const profileData = {
        email: user.email,
        full_name: user.user_metadata?.full_name || 
                  (user.user_metadata?.first_name && user.user_metadata?.last_name ? 
                    `${user.user_metadata.first_name} ${user.user_metadata.last_name}` : 
                    user.email),
        updated_at: new Date().toISOString()
      }

      console.log('Updating profile with data:', profileData)

      // Update the profile
      const { data, error } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('id', user.id)
        .select()
        .single()

      if (error) throw error

      // Clear cache after successful profile update
      clearUserCache()

      console.log('Profile synced successfully:', data)
      return data
    } catch (error) {
      console.error('Error syncing profile:', error)
      handleSupabaseError(error)
    }
  },

  async me() {
    try {
      const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV
      // Get cached user data from auth (this will include updated metadata)
      const user = await getCachedUser(supabase)
      if (!user) return null

      if (isDev) {
        console.log('Auth user:', user)
        console.log('Auth user metadata:', user.user_metadata)
        console.log('Auth user full_name:', user.user_metadata?.full_name)
      }

      // Get additional user data from profiles table
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()
      
      // Handle profile errors more gracefully
      if (error) {
        if (error.code === 'PGRST116') {
          // Profile not found - this is okay, will use auth data only
          console.warn('Profile not found for user:', user.id);
        } else {
          // Log error but don't throw - allow user to continue with auth data only
          console.error('Error fetching profile:', error);
          console.error('Error details:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          console.log('⚠️ Continuing with auth data only. Profile features may be limited.');
        }
      }
      
      if (isDev) console.log('Profile data:', data)
      
      const mergedUser = {
        ...user,
        ...data,
        // Ensure full_name is accessible at the top level
        full_name: user.user_metadata?.full_name || data?.full_name || user.full_name,
        // Ensure role is set for authorization
        role: data?.role || 'user'
      };
      
      if (isDev) {
        console.log('Merged user:', mergedUser)
        console.log('Final full_name:', mergedUser.full_name)
      }
      
      return mergedUser;
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async searchUsers(query) {
    try {
      const currentUser = await getCurrentUser()
      if (!currentUser) throw new Error('User not authenticated')

      if (!query || query.trim().length < 2) {
        return []
      }

      const searchTerm = query.trim().toLowerCase()
      
      // Build search query - search by email, name, or phone
      // Note: Phone search may be limited if phone isn't stored in profiles
      let searchQuery = supabase
        .from('profiles')
        .select('id, email, full_name, avatar_url')
        .or(`email.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%`)
        .limit(20)

      // Exclude current user from results
      searchQuery = searchQuery.neq('id', currentUser.id)

      const { data, error } = await searchQuery

      if (error) {
        console.error('Error searching users:', error)
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        })
        
        // Provide more specific error message for RLS issues
        if (error.code === '42501' || error.message?.includes('permission denied') || error.message?.includes('policy')) {
          throw new Error('Unable to search users. Please contact support if this issue persists.')
        }
        
        throw error
      }

      // Filter out null/empty results and return
      return (data || []).filter(user => user && (user.email || user.full_name))
    } catch (error) {
      console.error('User.searchUsers error:', error)
      // Re-throw to let the UI handle it
      throw error
    }
  },

  async signUp(email, password, metadata = {}) {
    try {
      console.log('🚀 Starting signup process for:', email);

      // Create the auth user
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      })
      
      if (error) throw error
      
      console.log('✅ Auth signup successful:', data.user?.id);

      // Create profile manually
      if (data.user && data.user.id) {
        try {
          // Wait a moment for the auth user to be fully created
          await new Promise(resolve => setTimeout(resolve, 100))
          
          const profileData = {
            id: data.user.id,
            email: data.user.email,
            full_name: metadata.full_name || 
                      (metadata.first_name && metadata.last_name ? 
                        `${metadata.first_name} ${metadata.last_name}` : 
                        data.user.email),
            terms_accepted_at: metadata.terms_accepted_at || null,
            privacy_accepted_at: metadata.privacy_accepted_at || null
          }
          
          console.log('📝 Creating profile for:', data.user.email);

          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .upsert(profileData, { 
              onConflict: 'id',
              ignoreDuplicates: false 
            })
            .select()
            .single()
          
          if (profileError) {
            console.error('❌ Profile creation error:', profileError)
            throw profileError;
          } else {
            console.log('✅ Profile created successfully:', profile.id);
          }
          
        } catch (profileError) {
          console.error('❌ Profile creation failed:', profileError);
          
          // If profile creation fails after auth user is created, we have an orphaned user
          console.error('⚠️ Orphaned auth.users record created for:', email);
          console.error('Admin should clean up orphaned auth users periodically');
          
          throw profileError;
        }
      }
      
      // Send welcome email asynchronously (don't block signup flow)
      if (data.user && data.user.email) {
        // Fire-and-forget: don't await, don't block user experience
        Promise.resolve().then(async () => {
        try {
          const userName = metadata.full_name || 
                          (metadata.first_name && metadata.last_name ? 
                            `${metadata.first_name} ${metadata.last_name}` : 
                            data.user.email.split('@')[0])
          const signInUrl = `${window.location.origin}/auth/login`
          
          // Import SendEmail dynamically to avoid circular dependencies
          const { SendEmail } = await import('./integrations')
          
          await SendEmail({
            to: data.user.email,
            template: 'welcome',
            data: {
              userName,
              userEmail: data.user.email,
              discoverUrl: signInUrl, // Changed from /discover to /auth/login
              baseUrl: window.location.origin
            }
          })
          
          console.log('✅ Welcome email sent successfully')
        } catch (emailError) {
          // Log error but don't fail the signup
          console.warn('⚠️ Failed to send welcome email:', emailError.message)
          // Signup was successful, email failure is non-critical
        }
        }).catch(err => {
          console.warn('⚠️ Email sending promise error:', err)
        })
        
        // DO NOT AWAIT - return immediately after starting email send
        console.log('📧 Welcome email queued for async delivery')
      }
      
      console.log('✅ Signup completed successfully');
      return data
    } catch (error) {
      console.error('❌ Signup failed:', error);
      
      // Provide user-friendly error messages
      if (error.message?.includes('already registered')) {
        throw new Error('This email is already registered. Please sign in instead.');
      }
      
      // Re-throw other errors
      throw error;
    }
  },

  async signIn(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      if (error) throw error
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async signOut() {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      return true
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async updateProfile(updates) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('profiles')
        .upsert({ id: user.id, ...updates })
        .select()
        .single()
      if (error) throw error
      
      // Clear cache after successful profile update
      clearUserCache()
      
      return data
    } catch (error) {
      handleSupabaseError(error)
    }
  },

  async updateTermsAndPrivacyAcceptance(termsAccepted, privacyAccepted) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      const now = new Date().toISOString()
      const updates = {}
      
      if (termsAccepted) {
        updates.terms_accepted_at = now
      }
      if (privacyAccepted) {
        updates.privacy_accepted_at = now
      }

      // Guard against empty updates object
      if (Object.keys(updates).length === 0) {
        // No updates to make, return current user profile
        const { data, error } = await supabase
          .from('profiles')
          .select()
          .eq('id', user.id)
          .single()
        
        if (error) {
          handleSupabaseError(error)
          throw error
        }
        return data
      }

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()
      
      if (error) {
        // Check if the error is about missing columns
        if (error.message && (error.message.includes('privacy_accepted_at') || error.message.includes('terms_accepted_at'))) {
          const migrationError = new Error('Database schema is missing required columns. Please run the migration: sql/add_terms_privacy_acceptance.sql')
          handleSupabaseError(migrationError)
          throw migrationError
        }
        handleSupabaseError(error)
        throw error
      }
      
      // Clear cache after successful profile update
      clearUserCache()
      
      return data
    } catch (error) {
      // handleSupabaseError already throws, so we just re-throw
      throw error
    }
  },

  async updateMyUserData(updates) {
    try {
      const user = await getCurrentUser()
      if (!user) throw new Error('User not authenticated')

      console.log('Updating user data:', { userId: user.id, updates })

      // Handle both auth metadata updates (like full_name, email) and profile data
      const authUpdates = {}
      const profileUpdates = {}

      // Separate auth updates from profile updates
      if (updates.full_name) {
        authUpdates.data = { full_name: updates.full_name }
      }
      if (updates.email) {
        authUpdates.email = updates.email
      }

      // All other updates go to the users/profiles table
      Object.keys(updates).forEach(key => {
        if (key !== 'full_name' && key !== 'email') {
          profileUpdates[key] = updates[key]
        }
      })

      console.log('Auth updates:', authUpdates)
      console.log('Profile updates:', profileUpdates)
      console.log('Children data in profileUpdates:', profileUpdates.children)
      console.log('Children data type:', typeof profileUpdates.children)
      console.log('Children data length:', profileUpdates.children?.length)
      console.log('Children data JSON:', JSON.stringify(profileUpdates.children, null, 2))

      // Update auth metadata if needed
      if (Object.keys(authUpdates).length > 0) {
        console.log('Updating auth metadata...')
        const { error: authError } = await supabase.auth.updateUser(authUpdates)
        if (authError) {
          console.error('Auth update error:', authError)
          throw authError
        }
        console.log('Auth metadata updated successfully')
        // Clear cache after successful auth metadata update
        clearUserCache()
      }

      // Update profile data in profiles table (excluding email and full_name which are in auth)
      if (Object.keys(profileUpdates).length > 0) {
        const upsertData = { id: user.id, ...profileUpdates }
        console.log('Updating profile with data:', upsertData)
        console.log('Children data in upsertData:', upsertData.children)
        console.log('Children data JSON:', JSON.stringify(upsertData.children, null, 2))
        
        const { data, error } = await supabase
          .from('profiles')
          .upsert(upsertData, { 
            onConflict: 'id',
            ignoreDuplicates: false 
          })
          .select()
          .single()
        
        if (error) {
          console.error('Database update error:', error)
          console.error('Error details:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          })
          
          // Handle specific column missing error
          if (error.code === 'PGRST204') {
            throw new Error(`Database column missing: ${error.message}. Please run the database fix script.`)
          }
          
          throw error
        }
        
        // Clear cache after successful profile update
        clearUserCache()
        
        console.log('Profile updated successfully:', data)
        return data
      } else {
        console.log('No profile updates needed')
        return { id: user.id }
      }
      
    } catch (error) {
      console.error('updateMyUserData error:', error)
      handleSupabaseError(error)
    }
  }
};

// TrafficSource entity - handles UTM parameter and traffic source tracking
export const TrafficSource = {
  /**
   * Save UTM data to database
   * @param {Object} params - UTM parameters and traffic source data
   * @param {string} params.session_id - Session ID for anonymous tracking
   * @param {string} params.user_id - User ID if authenticated (optional)
   * @param {string} params.utm_source - Traffic source
   * @param {string} params.utm_medium - Marketing medium
   * @param {string} params.utm_campaign - Campaign name
   * @param {string} params.utm_term - Paid keyword (optional)
   * @param {string} params.utm_content - Creative identifier (optional)
   * @param {string} params.utm_id - Campaign ID (optional)
   * @param {string} params.utm_source_platform - Source platform (optional)
   * @param {string} params.utm_creative_format - Creative format (optional)
   * @param {string} params.utm_marketing_tactic - Marketing tactic (optional)
   * @param {string} params.landing_page - Landing page path
   * @param {string} params.referrer - HTTP referrer (optional)
   * @returns {Promise<Object>} Saved traffic source record
   */
  async save(params) {
    try {
      const { data, error } = await supabase
        .from('user_traffic_sources')
        .insert(params)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      // Check if table doesn't exist (PGRST205)
      if (error?.code === 'PGRST205') {
        console.warn('user_traffic_sources table does not exist. UTM tracking is disabled. Run the SQL migration: sql/create_user_traffic_sources_table.sql')
        return null
      }
      console.error('TrafficSource.save error:', error)
      handleSupabaseError(error)
    }
  },

  /**
   * Get traffic sources for a specific user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of traffic source records
   */
  async getByUserId(userId) {
    try {
      const { data, error } = await supabase
        .from('user_traffic_sources')
        .select('*')
        .eq('user_id', userId)
        .order('first_seen_at', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      // Check if table doesn't exist (PGRST205)
      if (error?.code === 'PGRST205') {
        console.warn('user_traffic_sources table does not exist. UTM tracking is disabled. Run the SQL migration: sql/create_user_traffic_sources_table.sql')
        return []
      }
      console.error('TrafficSource.getByUserId error:', error)
      handleSupabaseError(error)
    }
  },

  /**
   * Link anonymous traffic source data to a user
   * @param {string} sessionId - Session ID to find
   * @param {string} userId - User ID to link to
   * @returns {Promise<Array>} Updated traffic source records
   */
  async linkToUser(sessionId, userId) {
    try {
      // Find all anonymous records for this session
      const { data: records, error: findError } = await supabase
        .from('user_traffic_sources')
        .select('id')
        .eq('session_id', sessionId)
        .is('user_id', null)

      if (findError) {
        // Check if table doesn't exist (PGRST205)
        if (findError.code === 'PGRST205') {
          console.warn('user_traffic_sources table does not exist. UTM tracking is disabled. Run the SQL migration: sql/create_user_traffic_sources_table.sql')
          return []
        }
        throw findError
      }

      if (!records || records.length === 0) {
        return []
      }

      // Update all records to link to user and set converted_at
      const now = new Date().toISOString()
      const recordIds = records.map(r => r.id)

      const { data, error } = await supabase
        .from('user_traffic_sources')
        .update({
          user_id: userId,
          converted_at: now,
          updated_at: now
        })
        .in('id', recordIds)
        .select()

      if (error) throw error
      return data || []
    } catch (error) {
      // Check if table doesn't exist (PGRST205)
      if (error?.code === 'PGRST205') {
        console.warn('user_traffic_sources table does not exist. UTM tracking is disabled. Run the SQL migration: sql/create_user_traffic_sources_table.sql')
        return []
      }
      console.error('TrafficSource.linkToUser error:', error)
      handleSupabaseError(error)
    }
  },

  /**
   * Get traffic source by session ID
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object|null>} Traffic source record or null
   */
  async getBySessionId(sessionId) {
    try {
      const { data, error } = await supabase
        .from('user_traffic_sources')
        .select('*')
        .eq('session_id', sessionId)
        .order('first_seen_at', { ascending: false })
        .limit(1)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return null
        }
        if (error.code === 'PGRST205') {
          // Table doesn't exist
          console.warn('user_traffic_sources table does not exist. UTM tracking is disabled. Run the SQL migration: sql/create_user_traffic_sources_table.sql')
          return null
        }
        throw error
      }
      return data
    } catch (error) {
      // Check if table doesn't exist (PGRST205)
      if (error?.code === 'PGRST205') {
        console.warn('user_traffic_sources table does not exist. UTM tracking is disabled. Run the SQL migration: sql/create_user_traffic_sources_table.sql')
        return null
      }
      console.error('TrafficSource.getBySessionId error:', error)
      handleSupabaseError(error)
    }
  },

  /**
   * Update last_seen_at timestamp for a traffic source
   * @param {string} id - Traffic source record ID
   * @returns {Promise<Object>} Updated record
   */
  async updateLastSeen(id) {
    try {
      const { data, error } = await supabase
        .from('user_traffic_sources')
        .update({
          last_seen_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      // Check if table doesn't exist (PGRST205)
      if (error?.code === 'PGRST205') {
        console.warn('user_traffic_sources table does not exist. UTM tracking is disabled. Run the SQL migration: sql/create_user_traffic_sources_table.sql')
        return null
      }
      console.error('TrafficSource.updateLastSeen error:', error)
      handleSupabaseError(error)
    }
  }
};
